
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";

interface UserFormData {
  id?: string;
  name: string;
  email: string;
  password: string;
  access_level: 'usuario' | 'admin';
  active: boolean;
}

interface UserFormProps {
  initialData?: UserFormData;
  onClose: () => void;
  onSuccess: () => void;
}

export function UserForm({ initialData, onClose, onSuccess }: UserFormProps) {
  const [formData, setFormData] = useState<UserFormData>(
    initialData || {
      name: '',
      email: '',
      password: '',
      access_level: 'usuario',
      active: true,
    }
  );
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      if (initialData?.id) {
        // Atualização
        const { error } = await supabase
          .from('cat_users')
          .update({
            name: formData.name,
            email: formData.email,
            ...(formData.password ? { password: formData.password } : {}),
            access_level: formData.access_level,
            active: formData.active,
          })
          .eq('id', initialData.id);

        if (error) throw error;
        toast.success('Usuário atualizado com sucesso!');
      } else {
        // Criação
        const { error } = await supabase
          .from('cat_users')
          .insert([{
            name: formData.name,
            email: formData.email,
            password: formData.password,
            access_level: formData.access_level,
            active: formData.active,
          }]);

        if (error) throw error;
        toast.success('Usuário criado com sucesso!');
      }

      onSuccess();
      onClose();
    } catch (error) {
      console.error('Erro ao salvar usuário:', error);
      toast.error('Erro ao salvar usuário');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="name">Nome</Label>
        <Input
          id="name"
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          required
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="email">E-mail</Label>
        <Input
          id="email"
          type="email"
          value={formData.email}
          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
          required
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="password">
          {initialData ? 'Nova Senha (deixe em branco para manter a atual)' : 'Senha'}
        </Label>
        <Input
          id="password"
          type="password"
          value={formData.password}
          onChange={(e) => setFormData({ ...formData, password: e.target.value })}
          required={!initialData}
          minLength={6}
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="access_level">Nível de Acesso</Label>
        <Select
          value={formData.access_level}
          onValueChange={(value: 'usuario' | 'admin') => 
            setFormData({ ...formData, access_level: value })
          }
        >
          <SelectTrigger>
            <SelectValue placeholder="Selecione o nível de acesso" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="usuario">Usuário</SelectItem>
            <SelectItem value="admin">Administrador</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="flex items-center space-x-2">
        <Switch
          id="active"
          checked={formData.active}
          onCheckedChange={(checked) => 
            setFormData({ ...formData, active: checked })
          }
        />
        <Label htmlFor="active">Ativo</Label>
      </div>

      <div className="flex justify-end gap-2 pt-4">
        <Button variant="outline" type="button" onClick={onClose}>
          Cancelar
        </Button>
        <Button type="submit" disabled={isSubmitting}>
          {isSubmitting ? 'Salvando...' : initialData ? 'Atualizar' : 'Criar'}
        </Button>
      </div>
    </form>
  );
}
